import { differenceInCalendarDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '../../../components/common/icon/Icon';
import { ISubsidy } from '../../../interfaces/Subsidy.interfaces';
import { formatDate } from '../../../utils/formatter/formatDate.util';

interface SubsidyDetailViewProps {
  subsidy: ISubsidy;
}

export const SubsidyDetailView = ({ subsidy }: SubsidyDetailViewProps) => {
  const navigate = useNavigate();

  const getDeadlineInfo = () => {
    if (!subsidy.deadline) {
      return { text: '상시모집', color: '#FF3049' };
    }

    const today = new Date();
    const deadlineDate = new Date(subsidy.deadline);
    const diffDays = differenceInCalendarDays(deadlineDate, today);

    const color = diffDays < 7 ? '#FF3049' : '#232429';
    const text = diffDays === 0 ? '오늘' : `${diffDays}일 남음`;

    return { text, color };
  };

  const deadlineInfo = getDeadlineInfo();

  return (
    <Container>
      <HeaderSection>
        <TitleSection>
          <Title>{subsidy.title}</Title>
        </TitleSection>

        <MetaSection>
          <Region>{subsidy.isNationwide ? '전국' : `${subsidy.state ?? ''} ${subsidy.city ?? ''}`.trim()}</Region>
          <SplitBar>{'|'}</SplitBar>
          <Department>{[subsidy.department, subsidy.team].filter(Boolean).join(' · ')}</Department>
        </MetaSection>

        <InfoCard>
          {subsidy.category && (
            <InfoItem>
              <InfoLabel>{'지원금 종류'}</InfoLabel>
              <InfoValue>{subsidy.category}</InfoValue>
            </InfoItem>
          )}
          {subsidy.team && (
            <InfoItem>
              <InfoLabel>{'담당 팀'}</InfoLabel>
              <InfoValue>{subsidy.team}</InfoValue>
            </InfoItem>
          )}
          <InfoItem>
            <InfoLabel>{'마감일'}</InfoLabel>
            <InfoValue style={{ color: deadlineInfo.color }}>{deadlineInfo.text}</InfoValue>
          </InfoItem>
          {subsidy.postedAt && (
            <InfoItem>
              <InfoLabel>{'등록일'}</InfoLabel>
              <InfoValue>{formatDate(new Date(subsidy.postedAt))}</InfoValue>
            </InfoItem>
          )}
          {subsidy.link && (
            <InfoItem>
              <InfoLabel>{'공고 링크'}</InfoLabel>
              <InfoValue>
                <LinkButton onClick={() => window.open(subsidy.link!, '_blank')}>
                  {'홈페이지 바로가기'}
                  <Icon name={'Link'} width={14} height={14} fill={'#445EFF'} />
                </LinkButton>
              </InfoValue>
            </InfoItem>
          )}
        </InfoCard>

        {subsidy.content && (
          <ContentSection>
            <SectionTitle>{'공고 상세내용'}</SectionTitle>
            <Content>{subsidy.content}</Content>
          </ContentSection>
        )}

        {subsidy.previewLinks && subsidy.previewLinks.length > 0 && (
          <PreviewSection>
            <SectionTitle>{'공고 미리보기'}</SectionTitle>
            <PreviewList>
              {subsidy.previewLinks.map((link, index) => (
                <PreviewItem key={link} onClick={() => window.open(link, '_blank')}>
                  <PreviewImage src={link} alt={subsidy.previews?.[index] || `미리보기 ${index + 1}`} />
                </PreviewItem>
              ))}
            </PreviewList>
          </PreviewSection>
        )}

        {subsidy.attachmentLinks && subsidy.attachmentLinks.length > 0 && (
          <AttachmentSection>
            <SectionTitle>{'첨부파일'}</SectionTitle>
            <AttachmentList>
              {subsidy.attachmentLinks.map((link, index) => (
                <AttachmentItem key={link} onClick={() => window.open(link, '_blank')}>
                  <Icon name={'Document1'} width={16} height={16} fill={'#858894'} />
                  <AttachmentName>{subsidy.attachmentNames?.[index] || `첨부파일 ${index + 1}`}</AttachmentName>
                </AttachmentItem>
              ))}
            </AttachmentList>
          </AttachmentSection>
        )}
      </HeaderSection>

      <ButtonSection>
        <Button onClick={() => navigate('/subsidy/list')}>
          <Icon name={'Return'} width={20} height={20} />
          <ButtonText>{'목록으로 돌아가기'}</ButtonText>
        </Button>
      </ButtonSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  padding: 24px 16px;
  background-color: #ffffff;
  min-height: 100vh;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 20px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.h1`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-family: Pretendard;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
`;

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Department = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const Region = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
`;

const SplitBar = styled.span`
  color: var(--Light-Scheme-Monochrome-grey50, #babdca);
  font-size: 12px;
`;

const InfoCard = styled.div`
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;

  &.deadline {
    color: var(--light-scheme-color-primary-red, #ff3049);
  }
`;

const LinkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--light-scheme-color-primary-blue, #445eff);
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
`;

const ButtonSection = styled.div`
  width: 100%;
  margin-top: 16px;
`;

const Button = styled.button`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  gap: 8px;
  border-radius: 8px;
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 500;
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;
  border-top: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const SectionTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
`;

const Content = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  font-family: Pretendard;
  font-size: 15px;
  line-height: 160%;
  white-space: pre-wrap;
  word-break: break-all;
`;

const AttachmentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;
  border-top: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  border-radius: 8px;
  cursor: pointer;
`;

const AttachmentName = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  font-family: Pretendard;
  font-size: 14px;
`;

const PreviewSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 0;
  border-top: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const PreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewItem = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
  cursor: pointer;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;
